import React, { useEffect, useState, useMemo } from 'react';
import { Search, Truck, Calendar, Clock, MessageSquare, ChevronDown, ChevronUp, Filter, History, ArrowUpDown, ArrowUp, ArrowDown, CalendarDays, FileText, Download } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { cn, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

// Period filter options
const PERIOD_OPTIONS = [
  { value: 'all', label: 'Tout', days: null },
  { value: '7d', label: '7 jours', days: 7 },
  { value: '30d', label: '30 jours', days: 30 },
];

// Status configuration
const STATUS_CONFIG = {
  ok: { color: 'bg-green-500', label: 'OK', textColor: 'text-green-500' },
  a_surveiller: { color: 'bg-yellow-500', label: 'À surveiller', textColor: 'text-yellow-500' },
  en_maintenance: { color: 'bg-orange-500', label: 'Maintenance', textColor: 'text-orange-500' },
  immobilise: { color: 'bg-red-500', label: 'Immobilisé', textColor: 'text-red-500' }
};

// Format datetime
const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// History Entry Component - Compact version
const HistoryEntry = ({ entry, theme, onAddNote }) => {
  const [expanded, setExpanded] = useState(false);
  const statusConfig = STATUS_CONFIG[entry.new_status] || STATUS_CONFIG.ok;
  const prevStatusConfig = STATUS_CONFIG[entry.previous_status] || null;

  return (
    <div className={cn(
      "rounded-xl border transition-all",
      theme === 'dark' ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200 shadow-sm'
    )}>
      <div 
        className={cn(
          "p-3 cursor-pointer",
          theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-50'
        )}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Top row - Vehicle + Status change description */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className={cn("w-2.5 h-2.5 rounded-full shrink-0", statusConfig.color)} />
            <span className={cn("font-bold text-sm truncate", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
              {entry.immatriculation}
            </span>
            <span className={cn("text-xs truncate hidden sm:inline", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
              {entry.marque} {entry.modele}
            </span>
          </div>
          <div className="flex items-center gap-1 text-xs shrink-0">
            {prevStatusConfig && (
              <>
                <span className={prevStatusConfig.textColor}>{prevStatusConfig.label}</span>
                <span className={theme === 'dark' ? 'text-zinc-600' : 'text-gray-400'}>→</span>
              </>
            )}
            <span className={cn("font-medium", statusConfig.textColor)}>{statusConfig.label}</span>
          </div>
        </div>

        {/* Note preview if exists */}
        {entry.note && (
          <p className={cn(
            "text-xs mb-2 line-clamp-2",
            theme === 'dark' ? 'text-zinc-400' : 'text-gray-600'
          )}>
            {entry.note}
          </p>
        )}

        {/* Bottom row - Date left, Author right */}
        <div className="flex items-center justify-between text-[10px]">
          <div className={cn("flex items-center gap-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
            <Calendar size={10} />
            {formatDateTime(entry.changed_at)}
          </div>
          <div className={cn("flex items-center gap-1", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
            <span>par {entry.changed_by_name || 'Système'}</span>
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </div>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className={cn(
          "px-3 pb-3 border-t",
          theme === 'dark' ? 'border-white/10' : 'border-gray-100'
        )}>
          <div className="mt-3">
            {/* Note section */}
            <div className="flex items-center justify-between mb-2">
              <span className={cn("text-xs font-medium", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
                Note / Raison:
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddNote(entry);
                }}
                className="text-[10px] h-7 px-2"
              >
                <MessageSquare size={12} className="mr-1" />
                {entry.note ? 'Modifier' : 'Ajouter'}
              </Button>
            </div>
            {entry.note ? (
              <p className={cn(
                "text-xs p-2 rounded-lg",
                theme === 'dark' ? 'bg-zinc-800 text-zinc-300' : 'bg-gray-100 text-gray-700'
              )}>
                {entry.note}
              </p>
            ) : (
              <p className={cn("text-xs italic", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
                Aucune note
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function Maintenance() {
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('vehicles'); // 'vehicles' or 'history'
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [expandedVehicleId, setExpandedVehicleId] = useState(null);
  const [vehicleHistory, setVehicleHistory] = useState({});
  const [loadingHistory, setLoadingHistory] = useState({});
  const [sortOrder, setSortOrder] = useState('none'); // 'none', 'asc', 'desc'
  const [periodFilter, setPeriodFilter] = useState('all'); // 'all', '7d', '30d', '90d'
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportMonth, setReportMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [generatingReport, setGeneratingReport] = useState(false);
  const { hasRole } = useAuth();
  const { theme } = useTheme();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    await Promise.all([
      fetchVehicles(),
      fetchMaintenanceHistory()
    ]);
    setLoading(false);
  };

  const fetchVehicles = async () => {
    try {
      const response = await api.get('/vehicles');
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  useEffect(() => {
    let filtered = history;
    
    if (searchTerm) {
      filtered = filtered.filter(h =>
        h.immatriculation.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${h.marque} ${h.modele}`.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(h => h.new_status === statusFilter);
    }
    
    setFilteredHistory(filtered);
  }, [searchTerm, history, statusFilter]);

  const fetchMaintenanceHistory = async () => {
    try {
      // Try new endpoint first
      const response = await api.get('/vehicles/status-history/all');
      setHistory(response.data);
      setFilteredHistory(response.data);
    } catch (error) {
      console.error('Error fetching status history:', error);
      // Fallback to vehicles if no history exists
      await fetchVehiclesAsFallback();
    } finally {
      setLoading(false);
    }
  };

  const fetchVehiclesAsFallback = async () => {
    try {
      const response = await api.get('/vehicles');
      // Create mock history from vehicles
      const mockHistory = response.data.map(v => ({
        id: v.id,
        vehicle_id: v.id,
        immatriculation: v.immatriculation,
        marque: v.marque,
        modele: v.modele,
        previous_status: null,
        new_status: v.statut,
        changed_at: v.updated_at || v.created_at || new Date().toISOString(),
        changed_by_name: 'Système',
        note: null
      }));
      setHistory(mockHistory);
      setFilteredHistory(mockHistory);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      toast.error('Erreur lors du chargement');
    }
  };

  const fetchVehicleHistory = async (vehicleId) => {
    if (vehicleHistory[vehicleId]) {
      // Already loaded
      return;
    }
    
    setLoadingHistory(prev => ({ ...prev, [vehicleId]: true }));
    
    try {
      const response = await api.get(`/vehicles/${vehicleId}/status-history`);
      setVehicleHistory(prev => ({
        ...prev,
        [vehicleId]: response.data.history || []
      }));
    } catch (error) {
      console.error('Error fetching vehicle history:', error);
      // Create empty history
      setVehicleHistory(prev => ({
        ...prev,
        [vehicleId]: []
      }));
    } finally {
      setLoadingHistory(prev => ({ ...prev, [vehicleId]: false }));
    }
  };

  const toggleVehicleExpanded = async (vehicleId) => {
    if (expandedVehicleId === vehicleId) {
      setExpandedVehicleId(null);
    } else {
      setExpandedVehicleId(vehicleId);
      await fetchVehicleHistory(vehicleId);
    }
  };

  const handleAddNote = (entry) => {
    setSelectedEntry(entry);
    setNoteText(entry.note || '');
    setNoteDialogOpen(true);
  };

  const handleSaveNote = async () => {
    if (!selectedEntry) return;
    setSavingNote(true);
    try {
      await api.put(`/maintenance/history/${selectedEntry.id}/note`, { note: noteText });
      toast.success('Note enregistrée');
      // Update local state
      setHistory(prev => prev.map(h => 
        h.id === selectedEntry.id ? { ...h, note: noteText } : h
      ));
      setNoteDialogOpen(false);
    } catch (error) {
      console.error('Error saving note:', error);
      // Fallback: just update local state
      setHistory(prev => prev.map(h => 
        h.id === selectedEntry.id ? { ...h, note: noteText } : h
      ));
      toast.success('Note enregistrée localement');
      setNoteDialogOpen(false);
    } finally {
      setSavingNote(false);
    }
  };

  // Count by status
  const countByStatus = (status) => history.filter(h => h.new_status === status).length;
  
  // Filter history by period
  const filteredHistoryByPeriod = useMemo(() => {
    const selectedPeriod = PERIOD_OPTIONS.find(p => p.value === periodFilter);
    if (!selectedPeriod || selectedPeriod.days === null) {
      return history;
    }
    
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - selectedPeriod.days);
    
    return history.filter(h => {
      const changeDate = new Date(h.changed_at);
      return changeDate >= cutoffDate;
    });
  }, [history, periodFilter]);
  
  // Count changes per vehicle from filtered history
  const vehicleChangeCounts = useMemo(() => {
    const counts = {};
    filteredHistoryByPeriod.forEach(h => {
      counts[h.vehicle_id] = (counts[h.vehicle_id] || 0) + 1;
    });
    return counts;
  }, [filteredHistoryByPeriod]);
  
  // Filter and sort vehicles based on search and sort order
  const filteredVehicles = useMemo(() => {
    let result = vehicles.filter(v => {
      if (!searchTerm) return true;
      return v.immatriculation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
             `${v.marque} ${v.modele}`.toLowerCase().includes(searchTerm.toLowerCase());
    });
    
    // Sort by number of changes if sort order is set
    if (sortOrder !== 'none') {
      result = [...result].sort((a, b) => {
        const countA = vehicleChangeCounts[a.id] || 0;
        const countB = vehicleChangeCounts[b.id] || 0;
        return sortOrder === 'asc' ? countA - countB : countB - countA;
      });
    }
    
    return result;
  }, [vehicles, searchTerm, sortOrder, vehicleChangeCounts]);
  
  // Cycle through sort orders
  const toggleSortOrder = () => {
    setSortOrder(prev => {
      if (prev === 'none') return 'desc';
      if (prev === 'desc') return 'asc';
      return 'none';
    });
  };

  // Generate monthly PDF report
  const generateMonthlyReport = async () => {
    setGeneratingReport(true);
    try {
      const [year, month] = reportMonth.split('-').map(Number);
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0, 23, 59, 59);
      
      // Filter history for the selected month - only breakdowns and immobilized vehicles
      const monthlyData = history.filter(h => {
        const date = new Date(h.changed_at);
        const isInMonth = date >= startDate && date <= endDate;
        const isBreakdown = h.new_status === 'en_maintenance' || h.new_status === 'immobilise';
        return isInMonth && isBreakdown;
      });

      if (monthlyData.length === 0) {
        toast.error('Aucune panne ou immobilisation ce mois-ci');
        setGeneratingReport(false);
        return;
      }

      // Group by vehicle
      const groupedByVehicle = {};
      monthlyData.forEach(entry => {
        const key = entry.vehicle_id;
        if (!groupedByVehicle[key]) {
          groupedByVehicle[key] = {
            immatriculation: entry.immatriculation,
            marque: entry.marque,
            modele: entry.modele,
            incidents: []
          };
        }
        groupedByVehicle[key].incidents.push(entry);
      });

      // Get month name in French
      const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                          'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
      const monthName = monthNames[month - 1];

      // Generate HTML content for PDF
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Rapport Maintenance - ${monthName} ${year}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; background: #fff; color: #333; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #dc2626; padding-bottom: 20px; }
            .header h1 { color: #dc2626; font-size: 24px; margin-bottom: 5px; }
            .header p { color: #666; font-size: 14px; }
            .summary { display: flex; gap: 20px; margin-bottom: 30px; }
            .summary-card { flex: 1; padding: 15px; border-radius: 8px; text-align: center; }
            .summary-card.maintenance { background: #fed7aa; border: 2px solid #f97316; }
            .summary-card.immobilise { background: #fecaca; border: 2px solid #dc2626; }
            .summary-card h3 { font-size: 28px; margin-bottom: 5px; }
            .summary-card p { font-size: 12px; color: #666; }
            .vehicle-section { margin-bottom: 25px; page-break-inside: avoid; }
            .vehicle-header { background: #1f2937; color: white; padding: 12px 15px; border-radius: 8px 8px 0 0; }
            .vehicle-header h2 { font-size: 16px; }
            .vehicle-header span { font-size: 12px; opacity: 0.8; }
            .incidents-table { width: 100%; border-collapse: collapse; }
            .incidents-table th { background: #f3f4f6; padding: 10px; text-align: left; font-size: 12px; border: 1px solid #e5e7eb; }
            .incidents-table td { padding: 10px; font-size: 12px; border: 1px solid #e5e7eb; }
            .status-badge { display: inline-block; padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: bold; }
            .status-maintenance { background: #fed7aa; color: #9a3412; }
            .status-immobilise { background: #fecaca; color: #991b1b; }
            .note { background: #f9fafb; padding: 8px; border-radius: 4px; font-style: italic; color: #666; margin-top: 5px; }
            .footer { margin-top: 40px; text-align: center; font-size: 10px; color: #999; border-top: 1px solid #e5e7eb; padding-top: 15px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>RAPPORT DE MAINTENANCE</h1>
            <p>${monthName} ${year} - BYTRANSPORT</p>
          </div>
          
          <div class="summary">
            <div class="summary-card maintenance">
              <h3>${monthlyData.filter(h => h.new_status === 'en_maintenance').length}</h3>
              <p>Mise(s) en maintenance</p>
            </div>
            <div class="summary-card immobilise">
              <h3>${monthlyData.filter(h => h.new_status === 'immobilise').length}</h3>
              <p>Immobilisation(s)</p>
            </div>
          </div>
          
          ${Object.values(groupedByVehicle).map(vehicle => `
            <div class="vehicle-section">
              <div class="vehicle-header">
                <h2>${vehicle.immatriculation}</h2>
                <span>${vehicle.marque} ${vehicle.modele}</span>
              </div>
              <table class="incidents-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Raison / Note</th>
                    <th>Par</th>
                  </tr>
                </thead>
                <tbody>
                  ${vehicle.incidents.map(inc => `
                    <tr>
                      <td>${new Date(inc.changed_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                      <td>
                        <span class="status-badge ${inc.new_status === 'immobilise' ? 'status-immobilise' : 'status-maintenance'}">
                          ${inc.new_status === 'immobilise' ? 'Immobilisé' : 'Maintenance'}
                        </span>
                      </td>
                      <td>${inc.note || 'Aucune note'}</td>
                      <td>${inc.changed_by_name || '-'}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          `).join('')}
          
          <div class="footer">
            <p>Rapport généré le ${new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
            <p>BYTRANSPORT - Gestion de Flotte & Transport</p>
          </div>
        </body>
        </html>
      `;

      // Create a blob and download
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      
      // Open print dialog
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      }

      toast.success(`Rapport ${monthName} ${year} généré`);
      setReportDialogOpen(false);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Erreur lors de la génération du rapport');
    } finally {
      setGeneratingReport(false);
    }
  };

  // Generate month options for the last 12 months
  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                          'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
      const label = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
      options.push({ value, label });
    }
    return options;
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-zinc-800 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-red-500 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-1 py-4 md:p-0 pb-24 md:pb-0" data-testid="maintenance-page">
      {/* Search bar at top */}
      <div className="relative w-full max-w-md mx-auto mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-500" size={18} />
        <Input
          type="text"
          placeholder="Rechercher un véhicule..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={cn(
            "pl-9 h-10 w-full",
            theme === 'dark' ? 'bg-zinc-800/50 border-white/10 text-white placeholder:text-zinc-500' : 'bg-white border-gray-200'
          )}
          data-testid="search-input"
        />
      </div>

      {/* Period Filter + Sort + Report - same line */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {/* Period Filter */}
        <div className={cn(
          "flex items-center gap-1 rounded-lg p-1",
          theme === 'dark' ? 'bg-zinc-800/50' : 'bg-gray-100'
        )}>
          <CalendarDays size={14} className={cn("ml-2", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')} />
          {PERIOD_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => setPeriodFilter(option.value)}
              className={cn(
                "px-2.5 py-1 rounded-md text-xs font-medium transition-all",
                periodFilter === option.value
                  ? 'bg-red-500 text-white'
                  : theme === 'dark'
                    ? 'text-zinc-400 hover:text-white hover:bg-zinc-700'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white'
              )}
              data-testid={`period-filter-${option.value}`}
            >
              {option.label}
            </button>
          ))}
        </div>
        
        {/* Sort Button - icon only */}
        <button
          onClick={toggleSortOrder}
          className={cn(
            "flex items-center justify-center w-9 h-9 rounded-lg transition-all",
            sortOrder !== 'none'
              ? 'bg-red-500 text-white'
              : theme === 'dark' 
                ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          )}
          data-testid="sort-by-changes-btn"
          title={sortOrder === 'none' ? 'Trier par changements' : sortOrder === 'desc' ? 'Décroissant' : 'Croissant'}
        >
          {sortOrder === 'none' && <ArrowUpDown size={16} />}
          {sortOrder === 'desc' && <ArrowDown size={16} />}
          {sortOrder === 'asc' && <ArrowUp size={16} />}
        </button>
        
        {/* Report Button - icon only */}
        <button
          onClick={() => setReportDialogOpen(true)}
          className={cn(
            "flex items-center justify-center w-9 h-9 rounded-lg transition-all",
            theme === 'dark' 
              ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          )}
          title="Rapport mensuel"
        >
          <FileText size={16} />
        </button>
      </div>

      {/* View toggle - Par véhicule / Historique global */}
      <div className="flex justify-center mb-3">
        <div className={cn(
          "flex rounded-lg p-1",
          theme === 'dark' ? 'bg-zinc-800/50' : 'bg-gray-100'
        )}>
          <button
            onClick={() => setViewMode('vehicles')}
            className={cn(
              "px-4 py-2 rounded-md text-sm font-medium transition-all",
              viewMode === 'vehicles'
                ? theme === 'dark' ? 'bg-red-500 text-white' : 'bg-white text-gray-900 shadow'
                : theme === 'dark' ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            )}
          >
            <Truck size={16} className="inline mr-2" />
            Par véhicule
          </button>
          <button
            onClick={() => setViewMode('history')}
            className={cn(
              "px-4 py-2 rounded-md text-sm font-medium transition-all",
              viewMode === 'history'
                ? theme === 'dark' ? 'bg-red-500 text-white' : 'bg-white text-gray-900 shadow'
                : theme === 'dark' ? 'text-zinc-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
            )}
          >
            <History size={16} className="inline mr-2" />
            Historique global
          </button>
        </div>
      </div>

      {/* VEHICLES VIEW */}
      {viewMode === 'vehicles' && (
        <>
          {/* Vehicle count - centered */}
          <p className={cn("text-sm text-center mb-4", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
            {filteredVehicles.length} véhicule{filteredVehicles.length > 1 ? 's' : ''}
          </p>
          
          {/* Period info message */}
          {periodFilter !== 'all' && (
            <p className={cn("text-xs text-center", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
              Affichage des changements des {PERIOD_OPTIONS.find(p => p.value === periodFilter)?.label.toLowerCase()}
              {' '}({filteredHistoryByPeriod.length} changement{filteredHistoryByPeriod.length !== 1 ? 's' : ''} sur cette période)
            </p>
          )}
          
          <div className="space-y-2 max-w-4xl mx-auto">
            {filteredVehicles.map((vehicle) => {
              const statusConfig = STATUS_CONFIG[vehicle.statut] || STATUS_CONFIG.ok;
              const isExpanded = expandedVehicleId === vehicle.id;
              const vHistory = vehicleHistory[vehicle.id] || [];
              const isLoading = loadingHistory[vehicle.id];
              
              return (
                <div 
                  key={vehicle.id}
                  className={cn(
                    "rounded-xl border transition-all",
                    theme === 'dark' ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200 shadow-sm'
                  )}
                >
                  {/* Vehicle Header */}
                  <div 
                    className={cn(
                      "p-3 md:p-4 cursor-pointer",
                      theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                    )}
                    onClick={() => toggleVehicleExpanded(vehicle.id)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
                        <div className={cn("w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center shrink-0", statusConfig.color + '/20')}>
                          <Truck size={20} className={cn(statusConfig.textColor, "md:hidden")} />
                          <Truck size={24} className={cn(statusConfig.textColor, "hidden md:block")} />
                        </div>
                        <div className="min-w-0">
                          <p className={cn("font-bold text-sm md:text-base", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                            {vehicle.immatriculation}
                          </p>
                          <p className={cn("text-xs md:text-sm truncate", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
                            {vehicle.marque} {vehicle.modele}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 md:gap-4 shrink-0">
                        {/* Status - hidden on small mobile, shown as dot only */}
                        <div className="hidden sm:flex items-center gap-2">
                          <div className={cn("w-3 h-3 rounded-full", statusConfig.color)} />
                          <span className={cn("text-sm font-medium", statusConfig.textColor)}>
                            {statusConfig.label}
                          </span>
                        </div>
                        <div className={cn("sm:hidden w-2.5 h-2.5 rounded-full shrink-0", statusConfig.color)} />
                        
                        <div className={cn(
                          "flex items-center gap-1 px-1.5 md:px-2 py-0.5 md:py-1 rounded-full text-[10px] md:text-xs",
                          theme === 'dark' ? 'bg-zinc-800 text-zinc-400' : 'bg-gray-100 text-gray-600'
                        )}>
                          <History size={10} className="md:w-3 md:h-3" />
                          <span className="hidden sm:inline">{vehicleChangeCounts[vehicle.id] || 0} chg</span>
                          <span className="sm:hidden">{vehicleChangeCounts[vehicle.id] || 0}</span>
                        </div>
                        
                        {isExpanded ? (
                          <ChevronUp size={18} className={cn("shrink-0", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')} />
                        ) : (
                          <ChevronDown size={18} className={cn("shrink-0", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')} />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded History */}
                  {isExpanded && (
                    <div className={cn(
                      "border-t px-4 pb-4",
                      theme === 'dark' ? 'border-white/10' : 'border-gray-100'
                    )}>
                      {isLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-500"></div>
                        </div>
                      ) : vHistory.length === 0 ? (
                        <div className="py-8 text-center">
                          <p className={cn("text-sm", theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
                            Aucun historique de changement de statut
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3 mt-4">
                          <h4 className={cn("text-sm font-semibold mb-3", theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>
                            Historique des changements de statut
                          </h4>
                          {vHistory.map((entry, index) => {
                            const entryStatus = STATUS_CONFIG[entry.new_status] || STATUS_CONFIG.ok;
                            const prevStatus = STATUS_CONFIG[entry.previous_status];
                            
                            return (
                              <div 
                                key={entry.id || index}
                                className={cn(
                                  "p-3 rounded-lg border-l-4",
                                  entryStatus.color.replace('bg-', 'border-'),
                                  theme === 'dark' ? 'bg-zinc-800/50' : 'bg-gray-50'
                                )}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      {prevStatus && (
                                        <>
                                          <span className={cn("text-xs", prevStatus.textColor)}>{prevStatus.label}</span>
                                          <span className={theme === 'dark' ? 'text-zinc-600' : 'text-gray-400'}>→</span>
                                        </>
                                      )}
                                      <span className={cn("text-sm font-medium", entryStatus.textColor)}>
                                        {entryStatus.label}
                                      </span>
                                    </div>
                                    
                                    {entry.note && (
                                      <p className={cn("text-sm mt-2 italic", theme === 'dark' ? 'text-zinc-400' : 'text-gray-600')}>
                                        "{entry.note}"
                                      </p>
                                    )}
                                  </div>
                                  
                                  <div className="text-right">
                                    <p className={cn("text-xs", theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>
                                      {formatDateTime(entry.changed_at)}
                                    </p>
                                    <p className={cn("text-xs", theme === 'dark' ? 'text-zinc-600' : 'text-gray-400')}>
                                      par {entry.changed_by_name || 'Système'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* HISTORY VIEW */}
      {viewMode === 'history' && (
        <>
      <div className={cn(
        "flex flex-wrap items-center justify-center gap-4 p-4 rounded-xl border",
        theme === 'dark' ? 'bg-zinc-900/30 border-white/5' : 'bg-gray-50 border-gray-200'
      )}>
        <Filter size={16} className={theme === 'dark' ? 'text-zinc-400' : 'text-gray-500'} />
        
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="statusFilter"
            checked={statusFilter === 'all'}
            onChange={() => setStatusFilter('all')}
            className="w-4 h-4 accent-red-500"
          />
          <span className={cn("text-sm font-medium", theme === 'dark' ? 'text-white' : 'text-gray-700')}>
            Tous ({history.length})
          </span>
        </label>

        {Object.entries(STATUS_CONFIG).map(([status, config]) => (
          <label key={status} className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="statusFilter"
              checked={statusFilter === status}
              onChange={() => setStatusFilter(status)}
              className="w-4 h-4"
            />
            <div className={cn("w-3 h-3 rounded-full", config.color)} />
            <span className={cn("text-sm", theme === 'dark' ? 'text-white' : 'text-gray-700')}>
              {config.label} ({countByStatus(status)})
            </span>
          </label>
        ))}
      </div>

      {/* Count */}
      <p className={cn("text-sm text-center", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
        {filteredHistory.length} entrée{filteredHistory.length > 1 ? 's' : ''} dans l'historique
      </p>

      {/* History List */}
      <div className="space-y-3 max-w-4xl mx-auto">
        {filteredHistory.map((entry) => (
          <HistoryEntry
            key={entry.id}
            entry={entry}
            theme={theme}
            onAddNote={handleAddNote}
          />
        ))}
      </div>

      {filteredHistory.length === 0 && (
        <div className={cn(
          "text-center py-12 rounded-xl border max-w-4xl mx-auto",
          theme === 'dark' ? 'bg-zinc-900/50 border-white/10 text-zinc-500' : 'bg-white border-gray-200 text-gray-500'
        )}>
          Aucun historique trouvé
        </div>
      )}
        </>
      )}

      {/* Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedEntry?.note ? 'Modifier la note' : 'Ajouter une note'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className={cn("text-sm mb-2", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
                Véhicule: <strong>{selectedEntry?.immatriculation}</strong>
              </p>
              <p className={cn("text-sm", theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
                Statut: <strong className={STATUS_CONFIG[selectedEntry?.new_status]?.textColor}>
                  {STATUS_CONFIG[selectedEntry?.new_status]?.label}
                </strong>
              </p>
            </div>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Décrivez la raison du changement d'état..."
              className={cn(
                "w-full h-32 p-3 rounded-lg border resize-none",
                theme === 'dark' 
                  ? 'bg-zinc-800 border-white/10 text-white placeholder:text-zinc-500' 
                  : 'bg-white border-gray-200'
              )}
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setNoteDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleSaveNote} disabled={savingNote}>
                {savingNote ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className={cn(
          "max-w-md",
          theme === 'dark' ? 'bg-zinc-900 border-white/10' : 'bg-white'
        )}>
          <DialogHeader>
            <DialogTitle className={cn("flex items-center gap-2", theme === 'dark' ? 'text-white' : 'text-gray-900')}>
              <FileText className="text-red-500" size={20} />
              Rapport Maintenance Mensuel
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className={cn("text-sm", theme === 'dark' ? 'text-zinc-400' : 'text-gray-600')}>
              Générez un rapport PDF des véhicules en panne ou immobilisés pour un mois donné.
            </p>
            
            <div>
              <label className={cn("block text-sm font-medium mb-2", theme === 'dark' ? 'text-zinc-300' : 'text-gray-700')}>
                Sélectionnez le mois
              </label>
              <Select value={reportMonth} onValueChange={setReportMonth}>
                <SelectTrigger className={cn(
                  theme === 'dark' ? 'bg-zinc-800 border-white/10' : 'bg-white border-gray-300'
                )}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className={cn(
                  theme === 'dark' ? 'bg-zinc-800 border-white/10' : 'bg-white'
                )}>
                  {monthOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className={cn(
              "p-3 rounded-lg text-sm",
              theme === 'dark' ? 'bg-zinc-800 text-zinc-300' : 'bg-gray-100 text-gray-700'
            )}>
              <p className="font-medium mb-1">Le rapport inclura :</p>
              <ul className="text-xs space-y-1 list-disc list-inside">
                <li>Véhicules passés en maintenance</li>
                <li>Véhicules immobilisés</li>
                <li>Dates et raisons des pannes</li>
                <li>Responsable de chaque déclaration</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setReportDialogOpen(false)}
              className={theme === 'dark' ? 'border-white/10' : ''}
            >
              Annuler
            </Button>
            <Button
              onClick={generateMonthlyReport}
              disabled={generatingReport}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {generatingReport ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              ) : (
                <Download size={16} className="mr-2" />
              )}
              Générer le PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
